// components
import Logo from '@components/UI/Logo';
import MenuButton from '@components/UI/MenuButton';
import ShapeButton from '@components/UI/ShapeButton';
import { motion } from 'framer-motion';
import CurrentUser from '@layout/Panel/CurrentUser';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';

// hooks
import useWindowSize from '@hooks/useWindowSize';
import usePanelScroll from '@hooks/usePanelScroll';
import { useSidebarContext } from '@contexts/sidebarContext';
import { useRef, useEffect } from 'react';
import { useNotification } from '@hooks/useNotification';
import NotificationWidget from '@widgets/NotificationWidget';
import { ContentTitle } from '@layout/Page/style';
import { QtyBadge } from '@components/UI/Badge/style';
import Grid from '@mui/material/Grid';
import SearchBar from '@widgets/SearchWidget';
import { colors } from '@styles/vars';
import { Actions, Header, LinksWrapper } from './style';
import BreadCrumb from '@widgets/BreadCrumbs';

const EnableBreadsCrumbPages = [
  "Profile",
  "Change Email",
  "Change Password",
  "Contact Us",
  "Privacy Policy",
  "Add Template",
  "Edit Template",
  "Add User",
  "Edit User",
  "Add Clinic",
  "Edit Clinic",
  "Edit Lead",
  "Edit API Integration Details",
  "Add Phrases",
  "FAQs",
  "Tutorial",
  "Traffic Drop",
  "Keyword History",
  "Deleted Leads",
  "Patients",
  "Patient Record"
]

const Panel = (props) => {
  const host = window.location.origin;
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const laptopL = width < 1279;
  const isDesktop = width >= 1366;
  const classname = usePanelScroll();
  const { isSidebarOpen } = useSidebarContext();
  const headerRef = useRef(null);
  const { getNotifications } = useNotification();
  const navigate = useNavigate();

  const MenuLogo = {
    width: '100px',
    display: 'flex',
    justifyContent: isMobile ? 'flex-start' : 'space-evenly',
    alignItems: 'center'
  };

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--header-height',
      `${headerRef.current.offsetHeight}px`
    );
  }, [width]);

  // get notification count once on load (superuser only, no real-time/polling)
  useEffect(() => {
    if (props.loggedInUserDetail.role === 'Super Admin') {
      getNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Header
      ismobile={isMobile ? 1 : 0}
      laptopL={laptopL}
      as={motion.header}
      animate={{ y: isSidebarOpen && isMobile ? '-100%' : 0 }}
      transition={{ duration: 0.3, ease: 'linear', type: 'tween' }}
      className={classname}
      ref={headerRef}
      paddingleft={props.paddingLeft}
    >
      <Grid
        container
        alignItems="center"
        // spacing={2}
        justifyContent="space-between"
        direction="row"
        sx={{ background: colors.aliceblue }}
      >
        {/* TUTORIAL HEADING */}
        {props.title !== "Tutorial" ?
          <Grid
            item
            xs={12}
            sm={12}
            style={{ fontSize: isMobile ? '12px' : '14px', textAlign: 'center', padding: '7px 1px', background: '#ffffff', borderRadius: '6px', marginBottom: isMobile ? '6px' : '20px', marginTop: width > 1279 ? '-25px' : null }}
          >
            <span> Start Exploring Our {isMobile ? 'App' : 'Website'}, To View Tutorial.&nbsp;<Link to="/app-tutorial" style={{ textDecoration: 'underline', color: colors.blue }}>Click Here</Link> </span>

          </Grid>
          : null}
        {/* LOGO IN MOBILE VIEW */}
        {
          isMobile ? (
            <Grid
              item
              xs={4}
              sm="auto"
              alignItems="center"
            >
              <Logo compact={isMobile} hideText={true} />
            </Grid>
          ) :
            null
        }
        {/* BREADSCRUMB ON CONTACT US PAGE, PRIVACY POLICY PAGE, IN DESKTOP VIEW */}
        {(EnableBreadsCrumbPages.includes( props.title)) && !isMobile ?
          <Grid
            item
            // A page with a search box shares this row with it. 8 columns would push the
            // search onto its own line, so the crumb takes only what it needs - the same
            // "auto" the page title uses on the lead list, which leaves the search sitting
            // in the middle of the row.
            sm={props.searchMenu ? 'auto' : props.title === 'FAQs' ? 1 : 8}
            alignItems="center"
          >
            <BreadCrumb
              isMobile={props.currentURL ? false : true}
              match={[
                { pathname: '/home', title: 'Home' },
                ...(props.parentCrumb
                  ? [{ pathname: '#', title: props.parentCrumb.title, onClick: props.parentCrumb.onClick }]
                  : []),
                { pathname: props.currentURL , title: props.currentPage ? props.currentPage : '' }
              ]}
            />
          </Grid>
          :
          width > 0 && !isMobile && !!props.title && (
            <Grid
              item
              xs={props.searchMenu ? 4 : 'auto'}
              sm="auto"
              alignItems="center"
            >
              <ContentTitle title={props.title}>
                {props.title}{' '}
                {props.hasBadge && props.qty && props.qty > 0 && (
                  <QtyBadge>+{props.qty}</QtyBadge>
                )}
              </ContentTitle>
            </Grid>
          )}
      {/* IF SEARCH BAR IN DESKTOP */}
        {props.searchMenu && !isMobile ? (
          <Grid
            item
            xs={6}
            sm={6}
            alignItems="center"
          >
            <SearchBar
              isMobile={isMobile}
              Search={props.Search}
              SetSearch={props.SetSearch}
              SearchText={props.SearchText}
              setSearchText={props.setSearchText}
            />
          </Grid>
        ) : null}
        {/* FAQ & USER SECTION */}
        <Grid
          item
          xs={isMobile ? 8 : 'auto'}
          sm={isMobile ? 8 : 'auto'}
          style={{ display: 'flex', justifyContent: 'flex-end' }}
          alignItems="center"
        >
          {isMobile ? (
            // <MenuButton />
            <>
              <Actions isMobile={isMobile}>
                <LinksWrapper isMobile={isMobile} style={{ marginRight: '8px', marginTop: 0, marginBottom: 0 }}>
                  <Link
                    to={'/faq'}
                    style={{ textDecoration: 'underline', textUnderlineOffset: '3px' }}
                  >
                    FAQs
                  </Link>
                </LinksWrapper>
                {props.loggedInUserDetail.role === 'Super Admin' && <NotificationWidget />}
                <CurrentUser isMobile={isMobile} width={width} {...props.loggedInUserDetail} />
              </Actions>
            </>
          ) : (
            <Actions isMobile={isMobile}>
              <LinksWrapper style={{ fontSize: '14px', marginTop: 0, marginBottom: 0 }}>
                <Link
                  to={'/faq'}
                  style={{ textDecoration: 'underline', textUnderlineOffset: '3px' }}
                >
                  FAQs
                </Link>
              </LinksWrapper>
              {props.loggedInUserDetail.role === 'Super Admin' && <NotificationWidget />}
              <CurrentUser width={width} {...props.loggedInUserDetail} />
            </Actions>
          )}
        </Grid>
      </Grid>
    </Header>
  );
};

export default Panel;
